import { QueryClient, dehydrate } from '@tanstack/react-query'
import { GetServerSideProps } from 'next'
import BasicHead from '../../../../../../components/common/BasicHead'
import LandingNav from '../../../../../../components/landing/navigation/LandingNav'
import MobileLandingNav from '../../../../../../components/landing/navigation/LandingNav/MobileLandingNav'
import EventDetailBody from '../../../../../../components/new-portal/event/EventDetailBody'
import { IEventDetail } from '../../../../../../components/new-portal/event/type'
import client from '../../../../../../modules/sanityClient'
import urls from '../../../../../../modules/urls'
import { buildSanityImage, getOgUrl } from '../../../../../../modules/utils'
import { FETCH_EVENT_DETAIL_INITIAL_DATA_QUERY } from '../../../../../../sanity/groqQueries'
import { FETCH_EVENT_DETAIL_INITIAL_DATA_QUERYResult } from '../../../../../../sanity/types'

const fetchInitialData = async (slug: string | string[]): Promise<any> => {
  try {
    const data: FETCH_EVENT_DETAIL_INITIAL_DATA_QUERYResult = await client.fetch(
      FETCH_EVENT_DETAIL_INITIAL_DATA_QUERY,
      { slug }
    )
    return data
  } catch (error) {
    console.error('Failed to fetch initial data:', error)
    throw error
  }
}

const EventDetail = ({ initialData, seoData, ogData }: IEventDetail) => {
  return (
    <div className="relative flex h-screen flex-col">
      <BasicHead seoData={seoData} ogData={ogData} />
      <div className="hidden lg:block">
        <LandingNav />
      </div>
      <div className="block lg:hidden">
        <MobileLandingNav />
      </div>
      <div className="relative mx-auto flex h-full w-full max-w-[1040px] flex-col">
        <EventDetailBody initialData={initialData} />
      </div>
    </div>
  )
}

export default EventDetail

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug, lang } = context.query

  if (slug.includes('webinar-event')) {
    const newPortalUrl = `/${lang}${urls.student.landing.study.readTogether.detail(slug)}`
    return {
      redirect: {
        destination: newPortalUrl,
        permanent: true,
      },
    }
  }

  const queryClient = new QueryClient()

  const initialData = await fetchInitialData(slug)

  const metaData = initialData.metaData

  const seoData = {
    pageTitle: metaData?.metaTitle,
    pageDescription: metaData?.metaDescription,
    pageKeywords: metaData?.metaKeyword,
  }

  const ogData = {
    title: metaData?.ogTitle,
    desc: metaData?.ogDescription,
    image: buildSanityImage(initialData.thumbnail).url(),
    ogUrl: getOgUrl({ lang, context: { pathname: context.resolvedUrl } }),
  }

  return {
    props: {
      initialData,
      seoData,
      ogData,
      slug,
      dehydratedState: dehydrate(queryClient),
    },
  }
}
